import { onlineWords } from './miss_category.js';
// State Farm auto-file (router spec docs/specs/2026-10-08-statefarm-confirm-then-file.md; Rod 2026-10-08 ~4:20 pm "if we have the info we
// go"): the console side. Everything our records state is filled by the robot; a request lists only the facts NO record states (or that
// records state differently). A person supplies each missing fact, once, and the robot files with no further review (dry run in this
// build). Who supplied what is audited. The router re-checks every fact against freshly read records, so this check is a first pass for a
// clear message, never the authority.
/** The console switch of the "supply the missing fact" press (console migration 006 adds it, off). */
export const SF_SUPPLY_SWITCH = 'button.sf_supply_info';
export const SF_FIELD_SHORT = {
    sf_party: 'State Farm driver', sf_at_fault: 'Who caused it', coverage_1p: 'Coverage', policy_number: 'Policy #', sf_vehicle_make: 'SF car make',
    vehicle_count: 'Vehicles', location: 'Where', sf_vehicle_action: 'What the SF car did', loss_of_life: 'Anyone died?', client_role: 'Client role',
    client_vehicle: 'Client car',
};
/** Why a request cannot be finished in the console, in plain words (the router's refusal codes). Unknown codes show as the code. */
export const SF_BLOCKER_TEXT = {
    NO_POLICE_REPORT: 'No police report, and filing without one is switched off',
    NO_REPORT_PARTY_BINDS: 'The police report names no State Farm party tied to this policy',
    GATE_TCR_TARGET_NOT_AT_FAULT: 'The police report does not mark the State Farm driver at fault',
    GATE_TCR_CLIENT_AT_FAULT: 'The police report marks our client at fault',
    GATE_TCR_TARGET: 'The police report does not name that person',
    GATE_TCR_POLICY_CONFLICT: 'The police report has a different State Farm policy number',
    GATE_POLICY_INTAKE_CONFLICT: 'The intake has a different policy number for the other driver',
    GATE_POLICY_IS_CLIENT_POLICY: "That policy number is our client's own",
    GATE_CASE_TYPE: 'The case type is not one the website lane files',
    GATE_SIDE: 'The insurance record is on the wrong side for this claim type',
    GATE_RECORD_TYPE: 'The insurance record type does not match the claim type',
    GATE_CARRIER: 'The carrier account is not a State Farm filing account',
    GATE_1P_LINK: 'The policyholder on the record is not our client',
    GATE_HOUSEHOLD: 'That person is in our client\'s household',
    GATE_TARGET_IS_CLIENT: 'That person is our client',
    TCR_REPORT_INCONSISTENT: 'Two reads of the police report disagree',
    ANSWER_POLICY_NUMBER_LIMIT: 'The policy number on file is not a State Farm number shape: fix it in Salesforce',
    ANSWER_POLICY_NUMBER_CONFLICT: 'The record and the AI call request have different policy numbers',
    ANSWER_INCIDENT_DATE_CONFLICT: 'The incident dates on file disagree',
    ANSWER_SF_VEHICLE_ACTION_UNSUPPORTED: 'The crash is not one the robot can describe (not a two-car crash)',
    ANSWER_VEHICLE_COUNT_NONMOTORIST: 'A pedestrian or cyclist case: the website lane does not file these',
    ANSWER_CLIENT_VEHICLE_MODEL_CONFLICT: "The records disagree on our client's car model",
    ANSWER_CLIENT_ROLE_SEAT_CONFLICT: "The records disagree on where our client sat",
    // 2026-10-13 (Kimi's UI assessment, change 7): codes the Trends page showed raw or lumped under "Other reason"
    GATE_CLAIM_EXISTS: 'A claim number already exists',
    GATE_CLAIM_EXISTS_SIBLING: 'Another record already has a claim for this policy',
    GATE_TIER_B_UNBOUND: 'Nothing on file ties this policy to the crash, and there is no police report',
    GATE_TIER_B_SIBLING_ROWS: 'Two records on the case could be this policy',
    GATE_TARGET_ROLE_CONFLICT: "The policyholder's role on the case does not fit the filing",
    GATE_TARGET_MISSING: 'No other driver is on file',
    ANSWER_POLICY_NUMBER_SHAPE: 'The policy number does not fit a State Farm format',
};
/** A blocker's words: its code's plain text, else the filing lane's generic words for the code, else "Other reason". */
export function sfBlockerText(code) {
    const c = String(code ?? '').trim();
    if (!c)
        return 'Other reason';
    if (SF_BLOCKER_TEXT[c])
        return SF_BLOCKER_TEXT[c];
    if (/^[A-Z][A-Z0-9_]+$/.test(c)) {
        const w = onlineWords(c);
        return w.charAt(0).toUpperCase() + w.slice(1);
    }
    return c;
}
export const SF_WHY_TEXT = {
    not_at_fault_on_report: 'not at fault on the report', not_the_other_vehicle_on_report: "our client's own vehicle on the report",
    report_row_has_no_policy_number: 'no policy number on the report', report_policy_differs: 'the report has another policy number',
    is_the_client: 'this is our client', not_on_police_report: 'not on the police report', no_police_report: 'no police report',
    not_city_and_state: 'not a city and state', not_a_state_farm_policy_shape: 'not a State Farm policy shape',
    incomplete_year_make_model: 'year, make or model missing', evidence_only: 'for reference',
};
const norm = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().toUpperCase() : JSON.stringify(v));
const sameValue = (key, a, b) => (key === 'vehicle_count' ? Number(a) === Number(b) : norm(a) === norm(b));
/** A first-pass check of one "supply the facts" press. -> the facts to record, or every problem with its field. */
export function validateSfSupply(row, input) {
    const problems = [];
    if (row.request.state !== 'needs_answers' || row.state !== 'needs_answers')
        problems.push({ key: null, code: 'request_not_open' });
    const out = [];
    for (const f of row.request.fields) {
        const a = input.answers[f.key];
        if (!a || a.value === undefined || a.value === null || a.value === '' || (Array.isArray(a.value) && !a.value.length)) {
            problems.push({ key: f.key, code: 'answer_missing' });
            continue;
        }
        if (a.chose === 'candidate' && !f.candidates.some((c) => c.selectable && sameValue(f.key, c.value, a.value))) {
            problems.push({ key: f.key, code: 'not_a_listed_value' });
            continue;
        }
        if (a.chose === 'staff_default' && (!f.staff_default || !sameValue(f.key, f.staff_default.value, a.value))) {
            problems.push({ key: f.key, code: 'not_the_staff_default' });
            continue;
        }
        if (f.options && f.input !== 'multi' && !f.options.some((o) => sameValue(f.key, o.value, a.value))) {
            problems.push({ key: f.key, code: 'not_an_option' });
            continue;
        }
        if (f.input === 'multi' && (!Array.isArray(a.value) || !a.value.every((v) => (f.options ?? []).some((o) => o.value === v)))) {
            problems.push({ key: f.key, code: 'not_an_option' });
            continue;
        }
        if (f.input === 'attest' && !(a.value && typeof a.value === 'object' && a.value.attested === true)) {
            problems.push({ key: f.key, code: 'attest_missing' });
            continue;
        }
        out.push({ key: f.key, raw: a.value, chose: a.chose });
    }
    return problems.length ? { ok: false, problems } : { ok: true, facts: out };
}
/** The open requests first (oldest first: the longest wait), then the blocked ones, then the rest. */
export function sfAnswersOrder(rows) {
    const rank = (r) => (r.request.state === 'needs_answers' ? 0 : r.request.state === 'blocked' ? 1 : 2);
    return [...rows].sort((a, b) => rank(a) - rank(b) || Date.parse(a.createdAt) - Date.parse(b.createdAt));
}
//# sourceMappingURL=sf_answers.js.map